import React from "react";
import {
  addDaysToYmd,
  formatDateShort,
  getBrazilCalendarWeekBoundsForDate,
  getTodayInBrazil,
} from "../../../utils/dateUtils";
import { Icons } from "../../shared/Icons";

interface WeekPeriodPickerProps {
  referenceDate: string;
  onChange: (referenceDate: string) => void;
  disabled?: boolean;
  className?: string;
}

const controlSurface =
  "border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100";

export const WeekPeriodPicker: React.FC<WeekPeriodPickerProps> = ({
  referenceDate,
  onChange,
  disabled = false,
  className = "",
}) => {
  const { startDate, endDate } =
    getBrazilCalendarWeekBoundsForDate(referenceDate);

  const goPrevWeek = () => {
    if (disabled) return;
    onChange(addDaysToYmd(referenceDate, -7));
  };

  const goNextWeek = () => {
    if (disabled) return;
    onChange(addDaysToYmd(referenceDate, 7));
  };

  const goCurrentWeek = () => {
    if (disabled) return;
    onChange(getTodayInBrazil());
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      onChange(value);
    }
  };

  const isCurrentWeek =
    getBrazilCalendarWeekBoundsForDate(getTodayInBrazil()).startDate ===
    startDate;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${className}`.trim()}
    >
      <div className="flex items-center gap-1.5 sm:gap-2">
        <button
          type="button"
          onClick={goPrevWeek}
          disabled={disabled}
          className={`flex-shrink-0 p-2 rounded-lg ${controlSurface} hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Semana anterior"
        >
          <Icons.ChevronLeft sx={{ fontSize: 22 }} />
        </button>

        <div
          className={`min-w-[12rem] sm:min-w-[14rem] px-3 py-2 text-sm sm:text-base font-semibold text-center rounded-lg ${controlSurface}`}
        >
          {formatDateShort(startDate)} – {formatDateShort(endDate)}
        </div>

        <button
          type="button"
          onClick={goNextWeek}
          disabled={disabled}
          className={`flex-shrink-0 p-2 rounded-lg ${controlSurface} hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Próxima semana"
        >
          <Icons.ChevronRight sx={{ fontSize: 22 }} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <label className="flex-1 sm:flex-initial">
          <span className="sr-only">Selecionar data da semana</span>
          <input
            type="date"
            value={referenceDate}
            onChange={handleDateInput}
            disabled={disabled}
            className={`w-full sm:w-auto px-3 py-2 text-sm rounded-lg ${controlSurface} hover:bg-gray-50 dark:hover:bg-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:[color-scheme:dark]`}
            aria-label="Selecionar qualquer dia da semana"
          />
        </label>

        {!isCurrentWeek && (
          <button
            type="button"
            onClick={goCurrentWeek}
            disabled={disabled}
            className="px-3 py-2 text-sm font-medium text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/60 hover:bg-sky-100 dark:hover:bg-sky-950 border border-sky-200 dark:border-sky-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Semana atual
          </button>
        )}
      </div>
    </div>
  );
};
